import { useQuery } from "@apollo/client";
import { CSSProperties, useCallback, useMemo, useRef } from "react";
import { SortDirection } from "react-aria-components";

import { EmptystateMagnetColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import Table from "components/Table";
import TableContextProvider from "components/Table/Context";
import TableVirtualizedSpacerRow from "components/Table/VirtualizedSpacerRow";
import TableRow from "components/Table/Row";
import TableWrapper from "components/Table/Wrapper";
import Box from "ds/components/Box";
import EmptyState from "ds/components/EmptyState";
import { Blueprint, Space } from "types/generated";

import { showIntegrationDrawerForm } from "./components/AttachDrawer";
import IntegrationListItem from "./components/ListItem";
import IntegrationsPageLayout from "./components/PageLayout";
import {
  ANALYTICS,
  INITIAL_COLUMNS_VIEW_CONFIG,
  INTEGRATIONS_COLUMN_CONFIG,
  INTEGRATIONS_COLUMNS_STORAGE_KEY,
  INTEGRATIONS_CUSTOM_END_COLUMN_CONFIG,
  ROW_HEADER_ID,
} from "./constants";
import { GET_BLUEPRINT_INTEGRATIONS } from "./gql";
import { BlueprintIntegrationData, GetBlueprintIntegrationsGql } from "./types";

type BlueprintIntegrationsProps = {
  blueprint: Blueprint;
  space: Space;
  isDraft: boolean;
};

const BlueprintIntegrations = ({ blueprint, space, isDraft }: BlueprintIntegrationsProps) => {
  const virtualizedListContainerRef = useRef<HTMLDivElement | null>(null);

  const { loading, data } = useQuery<GetBlueprintIntegrationsGql>(GET_BLUEPRINT_INTEGRATIONS, {
    variables: {
      id: blueprint.id,
    },
    skip: isDraft,
  });

  const integrations = useMemo(() => {
    const items = data?.blueprint?.externalIntegrations || [];

    const itemsWithSpace = items.map((item) => ({ ...item, space }));

    return itemsWithSpace;
  }, [data?.blueprint?.externalIntegrations, space]);

  const handleOnCreateClick = () => {
    showIntegrationDrawerForm({ blueprintId: blueprint.id, spaceId: space.id });
  };

  const sortDescriptor = {
    column: "symbol",
    direction: "ascending" as SortDirection,
  };

  const renderRow = useCallback(
    (
      item: Partial<BlueprintIntegrationData> & {
        virtIndex?: number;
        style?: CSSProperties;
        virtKey?: string;
        id: string;
        height?: number;
        ref?: (node: Element | null | undefined) => void;
      }
    ) => {
      {
        if ("height" in item && (item.id === "after" || item.id === "before")) {
          return <TableVirtualizedSpacerRow height={item.height} />;
        }

        return (
          <TableRow
            index={item.virtIndex}
            ref={item.ref}
            style={item.style}
            key={item.virtKey}
            id={item.id}
            name={item.integrationID}
          >
            <IntegrationListItem integration={item as BlueprintIntegrationData} />
          </TableRow>
        );
      }
    },
    []
  );

  return (
    <TableContextProvider
      columnsConfig={INTEGRATIONS_COLUMN_CONFIG}
      endCustomColumns={INTEGRATIONS_CUSTOM_END_COLUMN_CONFIG}
      initialColumnViewConfig={INITIAL_COLUMNS_VIEW_CONFIG}
      rowHeaderId={ROW_HEADER_ID}
      localStorageId={INTEGRATIONS_COLUMNS_STORAGE_KEY}
      setSortDescriptor={() => null}
      sortDescriptor={sortDescriptor}
      {...ANALYTICS}
    >
      <IntegrationsPageLayout
        onCreate={handleOnCreateClick}
        blueprint={blueprint}
        isDraft={isDraft}
      >
        {loading && !data && <PageLoading />}

        {!!integrations.length && (
          <TableWrapper virtualizedListContainerRef={virtualizedListContainerRef}>
            <Table
              ref={virtualizedListContainerRef}
              ariaLabel="Integrations list"
              items={integrations}
              loadingContent={loading}
            >
              {renderRow}
            </Table>
          </TableWrapper>
        )}

        {integrations.length === 0 && !loading && (
          <Box align="center" justify="center" grow="1" fullWidth>
            <EmptyState
              padding="large"
              icon={EmptystateMagnetColored}
              title={
                isDraft
                  ? "Please publish the blueprint before attaching any integrations."
                  : "No integrations attached yet."
              }
            />
          </Box>
        )}
      </IntegrationsPageLayout>
    </TableContextProvider>
  );
};

export default BlueprintIntegrations;
